import { useState } from 'react';
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Textarea } from '@rolefit/ui';
import { queries, type RoleRow } from '../lib/queries.js';
import { useAsync } from '../lib/useAsync.js';

// Outreach draft is grounded in the tailored package, so only these two checks
// can trip (the CV-only checks don't apply to an email).
const FLAG_LABELS: Record<string, string> = {
  banned_phrases: 'Uses generic filler phrases',
  no_fabrication: 'A claim could not be traced to a confirmed fact',
};
const flagDetail = (detail: unknown): string | null =>
  Array.isArray(detail) && detail.length > 0 ? detail.map(String).join(', ') : null;

function OutreachCard({ role, mailbox }: { role: RoleRow; mailbox: { enabled: boolean; canSend: boolean } }) {
  const draft = useAsync(() => queries.getOutreach(role.id), [role.id]);
  const [busy, setBusy] = useState<null | 'gen' | 'save' | 'approve' | 'send'>(null);
  const [editing, setEditing] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [to, setTo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sentNote, setSentNote] = useState<string | null>(null);

  async function approve() {
    setBusy('approve');
    setError(null);
    try {
      await queries.approveOutreach(role.id);
      draft.refetch();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Approve failed');
    } finally {
      setBusy(null);
    }
  }

  async function send() {
    setBusy('send');
    setError(null);
    try {
      const res = await queries.sendOutreach(role.id, to.trim());
      setSentNote(res.action === 'sent' ? 'Sent from your mailbox.' : 'Draft created in your mailbox.');
      draft.refetch();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Send failed');
    } finally {
      setBusy(null);
    }
  }

  async function generate() {
    setBusy('gen');
    setError(null);
    try {
      await queries.generateOutreach(role.id);
      draft.refetch();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Draft failed');
    } finally {
      setBusy(null);
    }
  }

  async function save() {
    setBusy('save');
    setError(null);
    try {
      await queries.saveOutreach(role.id, { subject, body_md: body });
      setEditing(false);
      draft.refetch();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setBusy(null);
    }
  }

  const d = draft.data;
  const failed = (d?.guardrail_report?.checks ?? []).filter((c) => !c.passed);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base">{role.title ?? 'Role'}</CardTitle>
          <Badge variant="success">package ready</Badge>
        </div>
        <CardDescription>{role.company}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {error && <p className="text-sm text-destructive">{error}</p>}

        {!d && !editing && (
          <Button size="sm" disabled={busy !== null} onClick={generate}>
            {busy === 'gen' ? 'Drafting…' : 'Draft outreach email'}
          </Button>
        )}

        {failed.length > 0 && (
          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs">
            <p className="mb-1 font-medium text-amber-700 dark:text-amber-300">Review before sending:</p>
            <ul className="space-y-1">
              {failed.map((c) => {
                const det = flagDetail(c.detail);
                return (
                  <li key={c.name} className="text-muted-foreground">
                    • {FLAG_LABELS[c.name] ?? c.name}
                    {det && <span className="text-foreground"> ({det})</span>}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {d && editing ? (
          <div className="space-y-2">
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" />
            <Textarea rows={10} value={body} onChange={(e) => setBody(e.target.value)} className="text-sm" />
            <div className="flex gap-2">
              <Button size="sm" disabled={busy !== null} onClick={save}>
                {busy === 'save' ? 'Saving…' : 'Save draft'}
              </Button>
              <Button size="sm" variant="ghost" disabled={busy !== null} onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          d && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                {d.subject && <p className="text-sm font-medium">{d.subject}</p>}
                {d.status === 'approved' && <Badge variant="secondary">approved</Badge>}
                {d.status === 'sent' && <Badge variant="success">sent</Badge>}
              </div>
              <pre className="max-h-56 overflow-auto whitespace-pre-wrap rounded-md border bg-muted/40 p-3 text-xs">
                {d.body_md}
              </pre>

              {d.status === 'draft' && (
                <div className="flex gap-2">
                  <Button size="sm" disabled={busy !== null} onClick={approve}>
                    {busy === 'approve' ? 'Approving…' : 'Approve'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy !== null}
                    onClick={() => {
                      setSubject(d.subject ?? '');
                      setBody(d.body_md);
                      setEditing(true);
                    }}
                  >
                    Edit
                  </Button>
                  <Button size="sm" variant="ghost" disabled={busy !== null} onClick={generate}>
                    {busy === 'gen' ? 'Drafting…' : 'Regenerate'}
                  </Button>
                </div>
              )}

              {d.status === 'approved' && (
                <div className="space-y-2 rounded-md border border-dashed p-3">
                  <p className="text-xs text-muted-foreground">
                    {mailbox.enabled
                      ? mailbox.canSend
                        ? 'Sends from your connected mailbox on your approval.'
                        : 'Creates a draft in your connected mailbox (draft-only scope).'
                      : 'Connect a mailbox in Settings to send. Nothing is sent without your approval.'}
                  </p>
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      placeholder="recruiter@company.com"
                      value={to}
                      onChange={(e) => setTo(e.target.value)}
                      className="h-8 text-xs"
                    />
                    <Button size="sm" disabled={busy !== null || !mailbox.enabled || !to.trim()} onClick={send}>
                      {busy === 'send' ? 'Working…' : mailbox.canSend ? 'Send email' : 'Create mailbox draft'}
                    </Button>
                  </div>
                </div>
              )}

              {sentNote && <p className="text-xs text-emerald-600 dark:text-emerald-400">{sentNote}</p>}
            </div>
          )
        )}
      </CardContent>
    </Card>
  );
}

export function OutreachPage() {
  const roles = useAsync(() => queries.listRoles(), []);
  const conns = useAsync(() => queries.getConnections(), []);
  const ready = (roles.data ?? []).filter((r) => r.has_package);
  const gmail = conns.data?.gmail ?? null;
  const mailbox = {
    enabled: Boolean(conns.data?.mailbox_enabled && gmail?.can_draft),
    canSend: gmail?.can_send ?? false,
  };

  return (
    <div className="mx-auto max-w-4xl p-6">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold">Outreach</h1>
        <p className="text-sm text-muted-foreground">
          Draft a recruiter email from your tailored package. Supervised only: review and edit here; sending from your
          mailbox is coming next, and nothing is ever sent without your approval.
        </p>
      </header>

      {roles.loading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {!roles.loading && ready.length === 0 && (
        <p className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
          No packages ready yet. Generate a package from the Pipeline to prepare outreach.
        </p>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        {ready.map((r) => (
          <OutreachCard key={r.id} role={r} mailbox={mailbox} />
        ))}
      </div>
    </div>
  );
}
